import { Controller } from "@hotwired/stimulus"
import { toast } from "../utils/toast"

export default class extends Controller {
  static targets = [
    "modal",
    "form",
    "idsContainer",
    "displayedCount",
    "selectedCount",
    "selectedScopeRadio",
    "selectedScopeLabel",
    "selectedHint",
    "displayedScopeRadio",
    "columnCheckbox"
  ]

  connect() {
    this.boundSelectionUpdated = this.selectionUpdated.bind(this)
    this.element.addEventListener("print-selection:updated", this.boundSelectionUpdated)
    this.selectionUpdated()
  }

  disconnect() {
    this.element.removeEventListener("print-selection:updated", this.boundSelectionUpdated)
  }

  open() {
    this.syncFormWithUrl()
    this.syncIdsForCurrentPage()
    this.selectionUpdated()
    this.modalTarget.showModal()
  }

  close() {
    this.modalTarget.close()
  }

  selectionUpdated() {
    const selectedIds = this.getSelectedIds()
    const pageIds = this.getPageIds()
    const selectedCount = selectedIds.length

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = String(selectedCount)
    }

    if (this.hasDisplayedCountTarget) {
      this.displayedCountTarget.textContent = String(pageIds.length)
    }

    if (this.hasSelectedScopeRadioTarget) {
      const enabled = selectedCount > 0
      this.selectedScopeRadioTarget.disabled = !enabled

      if (!enabled && this.selectedScopeRadioTarget.checked && this.hasDisplayedScopeRadioTarget) {
        this.displayedScopeRadioTarget.checked = true
      }
    }

    if (this.hasSelectedHintTarget) {
      this.selectedHintTarget.classList.toggle("hidden", selectedCount > 0)
    }

    if (this.hasSelectedScopeLabelTarget) {
      this.selectedScopeLabelTarget.classList.toggle("opacity-50", selectedCount === 0)
    }
  }

  async submit(event) {
    event.preventDefault()
    if (!this.hasFormTarget) return

    this.syncFormWithUrl()
    this.syncIdsForCurrentScope()

    const scopeType = this.selectedScopeType()
    if (scopeType === "selected" && this.getSelectedIds().length === 0) {
      toast("Select at least one record to export.", { type: "error" })
      return
    }

    if (this.hasColumnCheckboxTarget) {
      const selectedColumns = this.getSelectedColumnKeys()
      if (selectedColumns.length === 0) {
        toast("Select at least one column to export.", { type: "error" })
        return
      }
    }

    const form = this.formTarget
    const submitBtn = form.querySelector('button[type="submit"]')
    const originalText = submitBtn?.innerHTML

    try {
      if (submitBtn) {
        submitBtn.disabled = true
        submitBtn.innerHTML = '<span class="loading loading-spinner loading-sm"></span> Sending...'
      }

      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        }
      })

      if (response.ok) {
        toast("Export started. You will receive an email when it's ready.", { type: "success" })
        this.close()
      } else {
        const data = await response.json().catch(() => ({}))
        const message = Array.isArray(data.errors) ? data.errors.join(" ") : data.message || "Export failed. Please try again."
        toast(message, { type: "error" })
      }
    } catch (err) {
      toast("Export failed. Please try again.", { type: "error" })
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.innerHTML = originalText || "Send Export"
      }
    }
  }

  // Sync hidden fields with current URL params so filters/search/sort are captured
  syncFormWithUrl() {
    if (!this.hasFormTarget) return

    const url = new URL(window.location.href)
    const params = ["filters", "q", "sort_by", "sort_direction"]

    params.forEach((name) => {
      const value = url.searchParams.get(name)
      let input = this.formTarget.querySelector(`input[name="${name}"]`)
      if (value) {
        if (!input) {
          input = document.createElement("input")
          input.type = "hidden"
          input.name = name
          this.formTarget.insertBefore(input, this.formTarget.firstChild)
        }
        input.value = value
      } else if (input) {
        input.remove()
      }
    })
  }

  syncIdsForCurrentPage() {
    this.replaceIds(this.getPageIds())
  }

  syncIdsForCurrentScope() {
    const scopeType = this.selectedScopeType()

    if (scopeType === "all") {
      this.replaceIds([])
      return
    }

    if (scopeType === "selected") {
      this.replaceIds(this.getSelectedIds())
      return
    }

    this.replaceIds(this.getPageIds())
  }

  selectedScopeType() {
    const checked = this.formTarget?.querySelector('input[name="scope_type"]:checked')
    return checked?.value || "displayed"
  }

  replaceIds(ids) {
    if (!this.hasIdsContainerTarget) return

    this.idsContainerTarget.innerHTML = ""
    ids.forEach((id) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "ids[]"
      input.value = id
      this.idsContainerTarget.appendChild(input)
    })
  }

  getPageIds() {
    const printSelection = this.printSelectionController()
    if (printSelection?.rowCheckboxTargets?.length) {
      return printSelection.rowCheckboxTargets.map((cb) => cb.value).filter(Boolean)
    }

    if (!this.hasIdsContainerTarget) return []
    return Array.from(this.idsContainerTarget.querySelectorAll('input[name="ids[]"]')).map((input) => input.value).filter(Boolean)
  }

  getSelectedIds() {
    const printSelection = this.printSelectionController()
    if (printSelection) return printSelection.getSelectedIds()
    return []
  }

  getSelectedColumnKeys() {
    if (!this.hasColumnCheckboxTarget) return []

    return this.columnCheckboxTargets
      .filter((cb) => cb.checked)
      .map((cb) => cb.value)
  }

  printSelectionController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "print-selection")
  }
}
